import { FC, useMemo } from "react";
import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";
import {
  ChatAttachmentLibraryItemFragment,
  ChatAttachmentMediaFragment,
} from "@src/features/assistantChat/aiChat.generated";
import { ChatThumbnail } from "./ChatThumbnail";
import { AttachmentHoverCard } from "../ChatAttachMenu/ChatAttachMenuContent";
import { Tooltip, TooltipTrigger, TooltipContent } from "@src/lib/ui/Tooltip";
import { FileUpload } from "@src/features/aiAssist/hooks/useUploadFileAndExtractMarkdown";
import { LibraryItemTransform } from "@src/features/aiAssist/hooks/useLibraryItemDataTransform";
import XIcon from "@src/lib/icons/18px/x";
import { Button } from "@src/lib/ui/Button";
import { getLinkType, LinkType } from "@src/lib/utils/files";
import { Loader2Icon } from "lucide-react";
import { useLibraryItemTypeCopy } from "@src/features/contentLibrary/ContentAvatar";
import { TrainingResourceVersionType } from "@src/types.generated";

export type ChatAttachmentState = "uploading" | "loading" | "loaded";

export interface ChatAttachmentItem {
  libraryItemFragment?: ChatAttachmentLibraryItemFragment | null;
  mediaFragment?: ChatAttachmentMediaFragment | null;
  url?: string | null;
  file?: File;
  uploadingFile?: FileUpload;
  transformingLibraryItem?: LibraryItemTransform;
  hoverCardSide?: "top" | "bottom";
}

export interface ChatAttachmentProps extends ChatAttachmentItem {
  onRemove?: () => void;
  className?: string;
}

const ChatAttachment: FC<ChatAttachmentProps> = ({
  libraryItemFragment,
  mediaFragment,
  url,
  file,
  uploadingFile,
  transformingLibraryItem,
  onRemove,
  className,
  hoverCardSide = "bottom",
}) => {
  const { name, type } = useMemo(() => {
    // Transforming library item type
    if (transformingLibraryItem) {
      return {
        name:
          transformingLibraryItem.libraryItemFragment?.name.en ||
          "Processing...",
        type: "library" as const,
      };
    }

    // Uploading file type
    if (file) {
      return {
        name: file.name,
        type: "file" as const,
      };
    }

    // LibraryItem type
    if (libraryItemFragment) {
      return {
        name: libraryItemFragment.name.en,
        type: "library" as const,
      };
    }

    // Media type
    if (mediaFragment) {
      return {
        name: decodeURIComponent(
          mediaFragment.mediaUrls.en.split("/").pop() || "",
        ),
        type: "media" as const,
      };
    }

    // URL type
    if (url) {
      // Extract domain from URL for display
      let displayName = url;
      try {
        const urlObj = new URL(url);
        displayName = urlObj.hostname;
      } catch {
        // If URL parsing fails, use the full URL
      }

      return {
        name: displayName,
        type: "url" as const,
      };
    }

    if (uploadingFile) {
      return {
        name: uploadingFile.fileName,
        type: "file",
      };
    }

    // Fallback
    return {
      name: "Unknown",
      type: "unknown" as const,
    };
  }, [
    libraryItemFragment,
    mediaFragment,
    url,
    file,
    uploadingFile,
    transformingLibraryItem,
  ]);

  const shouldShowImagePreview = useMemo(() => {
    if (url) {
      const linkType = getLinkType(url);
      return [
        LinkType.IMAGE,
        LinkType.GIF,
        LinkType.MUX_VIDEO,
        LinkType.VIDEO,
        LinkType.HOSTED_VIDEO,
      ].includes(linkType);
    }

    if (mediaFragment?.mediaUrls?.en) {
      const linkType = getLinkType(mediaFragment.mediaUrls.en);
      return [
        LinkType.IMAGE,
        LinkType.GIF,
        LinkType.MUX_VIDEO,
        LinkType.VIDEO,
        LinkType.HOSTED_VIDEO,
      ].includes(linkType);
    }

    if (mediaFragment?.thumbnailImageUrl) return true;

    return false;
  }, [url, mediaFragment, libraryItemFragment]);

  const libraryItemTypeCopy = useLibraryItemTypeCopy();
  const label = useMemo(() => {
    if (libraryItemFragment) {
      if (
        libraryItemFragment.trainingResource?.draftVersion.type ===
        TrainingResourceVersionType.Media
      ) {
        const mediaUrl =
          libraryItemFragment.trainingResource?.draftVersion.media?.mediaUrls
            ?.en || "";
        const filename = mediaUrl.split("/").pop() || "";
        const fileExtension = filename.split(".").pop() || "";
        return {
          label: `${fileExtension.toUpperCase()} Resource`,
        };
      }
      if (
        libraryItemFragment.trainingResource?.draftVersion.type ===
        TrainingResourceVersionType.RichTextDocument
      ) {
        return {
          label: "Opus Doc",
        };
      }
      return {
        label: libraryItemTypeCopy[libraryItemFragment.type],
      };
    } else if (uploadingFile && !mediaFragment) {
      return {
        label: "",
      };
    }

    if (mediaFragment) {
      const mediaUrl = mediaFragment.mediaUrls?.en || "";
      const fileExtension = mediaUrl.split(".").pop() || "";

      return {
        label: `${fileExtension.toUpperCase()}`,
      };
    }

    return {
      label: "",
    };
  }, [libraryItemFragment, mediaFragment, uploadingFile]);
  const attachmentContent = (
    <div className=" flex relative min-w-[150px] w-[200px] max-w-[200px]">
      {onRemove && (
        <Button
          onClick={onRemove}
          variant="outline"
          size="icon"
          className="size-[18px] absolute -top-1.5 -right-1.5 z-10"
        >
          <XIcon className="text-foreground size-[12px]" />
        </Button>
      )}
      <div
        className={cn(
          "flex-1 bg-white inline-flex items-center px-[3px] py-0.5 pr-4 overflow-hidden  cursor-default transition-all duration-150 ease-in-out border border-gray-2 rounded-lg h-[40px] gap-2",
          uploadingFile?.state === "error" && "bg-red-1 border-red-5",
          transformingLibraryItem?.state === "error" && "bg-red-1 border-red-5",
          shouldShowImagePreview &&
            "flex items-center justify-center size-[40px] p-0",
          className,
        )}
      >
        <div className="flex items-center justify-center size-[32px] flex-shrink-0">
          {transformingLibraryItem?.state === "transforming" ? (
            <div className="flex items-center justify-center size-[32px]">
              <Loader2Icon className="size-4 text-primary animate-spin" />
            </div>
          ) : uploadingFile?.state === "uploading" ? (
            <div className="flex items-center justify-center size-[32px]">
              <Loader2Icon className="size-4 text-primary animate-spin" />
            </div>
          ) : (
            <ChatThumbnail
              libraryItemFragment={
                libraryItemFragment ||
                transformingLibraryItem?.libraryItemFragment
              }
              mediaFragment={mediaFragment}
              url={url}
              file={file}
              size={32}
              className={cn(
                "rounded-[6px]",
                uploadingFile?.state === "error" && "text-red-5",
                transformingLibraryItem?.state === "error" && "text-red-5",
              )}
            />
          )}
        </div>
        {!shouldShowImagePreview && (
          <div className="flex flex-col self-stretch flex-1 justify-center">
            <Text
              variant="fine-print-title"
              ellipsisParentFlexDirection="col"
              className="min-w-24 flex-1"
              ellipsis
            >
              {name}
            </Text>
            <Text
              variant="fine-print-description"
              ellipsisParentFlexDirection="col"
              className="min-w-24 flex-1 text-gray-6"
              ellipsis
            >
              {label.label}
            </Text>
          </div>
        )}
      </div>
    </div>
  );

  if (transformingLibraryItem) {
    // Show tooltip for transforming library item or error
    const tooltipText =
      transformingLibraryItem.state === "transforming"
        ? "Processing library item for chat..."
        : transformingLibraryItem.state === "error"
          ? transformingLibraryItem.error || "Failed to process library item"
          : name;
    return (
      <Tooltip>
        <TooltipTrigger asChild>{attachmentContent}</TooltipTrigger>
        <TooltipContent>{tooltipText}</TooltipContent>
      </Tooltip>
    );
  }

  if (uploadingFile) {
    // Show tooltip for uploading or error
    const tooltipText =
      uploadingFile.state === "uploading"
        ? "File upload in progress"
        : uploadingFile.state === "error"
          ? uploadingFile.error || "Upload failed"
          : name;
    return (
      <Tooltip>
        <TooltipTrigger asChild>{attachmentContent}</TooltipTrigger>
        <TooltipContent>{tooltipText}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <AttachmentHoverCard
      libraryItemFragment={libraryItemFragment}
      mediaFragment={mediaFragment}
      hoverCardContentProps={{
        side: hoverCardSide,
      }}
    >
      {attachmentContent}
    </AttachmentHoverCard>
  );
};

export default ChatAttachment;
